import { Trans } from "@lingui/react";
import { useScreenSize } from "@mobility-item-page/client";
import { DealerRatings, LocationSection } from "@mobility-item-page/dealer/client";
import type { RefObject } from "react";
import { useOrganisation } from "src/context/DealerDataProviderContext.tsx";
import { usePulseTracking } from "src/utils/pulseHelper.ts";
import { useAppConfig } from "../../context/AppConfigProviderContext.js";
import { TabId } from "../../utils/tabs.ts";
import { BrandAuthorizations } from "../Components/BrandAuthorizations.tsx";
import { Phonenumber } from "../Components/Buttons/Phonenumber.tsx";
import { CompanyDetails } from "../Components/CompanyDetails.tsx";
import { Description } from "../Components/Description.tsx";
import GoogleMapsSection from "../Components/GoogleMapsSection.js";
import { Icon } from "../Components/Icon.tsx";
import { Links } from "../Components/Links.tsx";
import { OpeningHours } from "../Components/OpeningHours.tsx";
import { SafetyElementHighlight } from "../Components/SafetyElementHighlight.tsx";
import "./about-us.css";
import "./sections.css";

export const AboutUs = ({ ref }: { ref: RefObject<HTMLDivElement | null> }) => {
  const { isMobile } = useScreenSize();
  const { name, openingHoursInfo, location, companyLogoUrl, uniqueSellingPoints, phoneNumber } = useOrganisation();
  const { googleMapsEmbed } = useAppConfig();
  const { trackDealerRatings, trackSeeLocation } = usePulseTracking();

  const showUSPs = uniqueSellingPoints.length > 0;

  return (
    <div id={TabId.AboutUs} className="about-us section" ref={ref}>
      <div className="about-us__content flex flex-col gap-40">
        <section>
          {companyLogoUrl && <img src={companyLogoUrl} alt={`${name} logo`} className="max-h-40 max-w-[160]" />}
          <h1 className="my-16">{name}</h1>
          <DealerRatings trackClick={trackDealerRatings} className="mb-8" />
          <CompanyDetails />
          <div className="w-full lg:w-[35%] flex gap-8 mt-24">
            <Phonenumber fixedPhoneNumber={phoneNumber} />
          </div>
        </section>
        <Description />
        {showUSPs && (
          <section>
            <h2 className="t3 mb-16">
              <Trans id="dealerBrandingLayout.offerings.label" message="We offer" />
            </h2>
            <ul className="grid mb-0 gap-12">
              {uniqueSellingPoints.map((sellingPoint) => (
                <li key={sellingPoint} className="flex items-center gap-8">
                  <Icon name="Check" />
                  <span>{sellingPoint}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      <div className="about-us__sidebar">
        <SafetyElementHighlight />
        <BrandAuthorizations />
        {!isMobile && <Links />}
      </div>
      <div className="about-us__bottom">
        <div className="grid md:grid-cols-2">
          {location?.mapInfo && !googleMapsEmbed && (
            <LocationSection trackClick={trackSeeLocation} location={location} />
          )}
          {location?.mapInfo && googleMapsEmbed && (
            <GoogleMapsSection mapsEmbed={googleMapsEmbed} location={location} trackClick={trackSeeLocation} />
          )}
          <OpeningHours className="md:pl-[64] mt-40 md:mt-0" openingHoursInfo={openingHoursInfo} />
        </div>
        {isMobile && <Links />}
      </div>
    </div>
  );
};
